import { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  APP_STORE_URL, PLAY_STORE_URL, CONTACT_EMAIL, INSTAGRAM_URL,
  LANGS, LANG_CODES, DEFAULT_LANG, langPath,
} from "../seo/site";
import Picture from "../Picture";
import { PRACTICE, practicePath } from "../practice/practiceContent";
import { ANALYTICS_ENABLED } from "../analytics/config";
import { openConsentSettings } from "../analytics/consent";
import { COOKIE_SETTINGS_LABEL } from "../analytics/ConsentBanner";

const IMG = {
  logo: "/images/icon.png",
  apple: "/images/Download_on_the_App_Store_Badge.svg",
  google: "/images/google-play-badge.png",
  screen1: "/images/MockUps/Screen4.png",
  screen2: "/images/MockUps/Screen5.png",
  screen3: "/images/MockUps/Screen2.png",
  avatar1: "/images/Avatars/Guy_1.png",
  avatar2: "/images/Avatars/Girl_1.png",
  avatar3: "/images/Avatars/Guy_2.png",
  reviewer1: "/images/reviewer1.jpg",
  reviewer2: "/images/reviewer2.jpg",
  reviewer3: "/images/reviewer3.jpg",
};

const FONT =
  '"Poppins", "Poppins Fallback", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

const C = {
  zinc900: "#18181b", zinc700: "#3f3f46", zinc600: "#52525b", zinc500: "#71717a",
  zinc400: "#a1a1aa", zinc300: "#d4d4d8", zinc200: "#e4e4e7", zinc100: "#f4f4f5", zinc50: "#fafafa",
  gray900: "#111827", gray700: "#374151", gray600: "#4b5563", gray500: "#6b7280", gray200: "#e5e7eb",
  amber: "#fbbf24", white: "#ffffff",
};

/* -------------------------------------------------------------------------- */
/* Languages + translations                                                    */
/* -------------------------------------------------------------------------- */

/* Non-translated per-item assets (images + icons + numbers) */
const STEP_IMAGES = [IMG.screen1, IMG.screen2, IMG.screen3];
const STEP_NUMS = ["01", "02", "03"];

/* Reviews: avatars + names + star ratings are the same across languages */
const REVIEW_AVATARS = [IMG.reviewer1, IMG.reviewer2, IMG.reviewer3];
const REVIEW_NAMES = ["Irina", "Olga", "Adam"];
const REVIEW_STARS = [5, 5, 5];

/* Pricing: amount (kr) + which card is highlighted are the same across languages */
const PLAN_META = [
  { amount: "149", featured: false },
  { amount: "249", featured: false },
  { amount: "399", featured: true },
];

const TR = {
  sv: {
    seo: {
      title: "Körkortsteori & Teoriprov B – Öva körkortsfrågor | Teorikörkort",
      description: "Träna körkortsteori och teoriprov för B-körkort. Öva på 500+ körkortsfrågor och alla vägmärken – på svenska, engelska, ukrainska eller ryska.",
    },
    nav: { home: "Hem", features: "Funktioner", faq: "Vanliga frågor", contact: "Kontakt" },
    a11y: { menu: "Meny", lang: "Byt språk", appStore: "Ladda ned i App Store", googlePlay: "Ladda ned på Google Play" },
    hero: {
      titlePre: "Klara ", titleEm: "teoriprovet.", titlePost: "Första försöket.",
      lead: "Teorikörkort är appen som tar dig genom kunskapsprovet. Läs teorin, öva på provliknande frågor och slå upp alla vägmärken — på svenska, engelska, ukrainska eller ryska.",
      altHome: "Teorikörkort — appens startskärm",
      altQuestion: "Teorikörkort — en övningsfråga med svarsalternativ",
      social: "Uppskattad av våra användare",
      buyLabel: "Köp direkt", buyNote: "Full tillgång låses upp inne i appen",
    },
    how: {
      tag: "Så fungerar det", titlePre: "Tre steg till ett ", titleEm: "godkänt", titlePost: " teoriprov",
      lead: "Kom igång på 30 sekunder. Börja plugga redan idag.",
      steps: [
        { title: "Öva på riktiga provfrågor", description: "Svara på provliknande flervalsfrågor med bild och svarsalternativ — precis som på kunskapsprovet. Du ser rätt svar och en förklaring direkt.", alt: "Teorikörkort — en övningsfråga med bild och svarsalternativ" },
        { title: "Läs teorin", description: "Gå igenom trafikreglerna kapitel för kapitel, med bilder, exempel och en komplett katalog över svenska vägmärken.", alt: "Teorikörkort — ett kapitel i teoridelen" },
        { title: "Öva tills du är redo", description: "Kör provliknande frågor och se resultat per ämnesområde. Statistiken visar exakt vad du behöver repetera.", alt: "Teorikörkort — resultatskärm efter ett övningsprov" },
      ],
    },
    features: {
      tag: "Mer än bara frågor", titlePre: "Byggd för att göra dig ", titleEm: "redo",
      items: [
        { name: "Alla vägmärken", description: "Bläddra och sök bland alla svenska vägmärken, indelade i kategorier med namn och förklaring." },
        { name: "Provliknande frågor", description: "Frågorna följer kunskapsprovets format och viktning, med en förklaring till varje svar." },
        { name: "Fyra språk", description: "Svenska, engelska, ukrainska och ryska — hela appen, inte bara menyerna. Byt språk när du vill." },
        { name: "Följ dina framsteg", description: "Resultat per ämnesområde och full provhistorik visar vad du behöver repetera innan provdagen." },
      ],
    },
    faq: {
      tag: "Har du frågor?", heading: "Vanliga frågor",
      items: [
        { question: "Hur många frågor finns i appen?", answer: "Över 500 övningsfrågor som täcker teoriprovets alla ämnesområden. Innehållet uppdateras löpande." },
        { question: "Liknar frågorna det riktiga kunskapsprovet?", answer: "Ja. Frågorna har samma flervalsformat som Trafikverkets kunskapsprov, och övningsproven följer provets viktning mellan ämnesområdena. Varje svar har en förklaring så att du förstår varför det är rätt." },
        { question: "Vilka språk stöds?", answer: "Svenska, engelska, ukrainska och ryska. Allt innehåll är översatt — teorikapitel, frågor och vägmärken — och du byter språk när som helst inne i appen." },
        { question: "Finns alla vägmärken med?", answer: "Ja. Appen har en sökbar katalog över de svenska vägmärkena, indelad i kategorier som varningsmärken, förbudsmärken och påbudsmärken, med namn och förklaring till varje märke." },
        { question: "Kan jag testa appen innan jag betalar?", answer: "Ja. Du kan köra ett kortare övningsprov utan konto för att se hur appen fungerar. Full tillgång till teorin och alla frågor låses upp med ett abonnemang." },
        { question: "Vilken behörighet gäller innehållet?", answer: "Innehållet är inriktat på teoriprovet för behörighet B (personbil) och bygger på gällande svenska trafikregler." },
      ],
    },
    reviews: {
      tag: "Omdömen", heading: "Uppskattad av eleverna",
      items: [
        { text: "Tydligt och aldrig överväldigande. Förklaringarna till felsvaren är toppen, och teorin täcker allt med exempel. Tar mitt körkort om ett par månader!", label: "Verifierad användare" },
        { text: "Riktigt hjälpsam inför det svenska körkortsprovet. Älskar att reglerna är översatta till ukrainska och ryska, och provläget känns precis som på riktigt. Den blir dessutom bara bättre. Rekommenderas verkligen.", label: "Verifierad användare" },
        { text: "Testade en massa teoriappar och den här är klart bäst. Enkel, allt bara känns logiskt. Klarade provet utan problem.", label: "Klarade första försöket" },
      ],
    },
    pricing: {
      tag: "Priser", heading: "Enkla priser", lead: "Ett köp, direkt i appen. Välj planen som passar dig.",
      plans: [
        { name: "1 månad", period: "per månad", note: "Perfekt för att komma igång", badge: "" },
        { name: "3 månader", period: "för 3 månader", note: "Vår populäraste plan", badge: "Populär" },
        { name: "Livstid", period: "engångsbetalning", note: "Betala en gång, din för alltid", badge: "Bäst värde" },
      ],
      buyLabel: "Köp direkt", buyNote: "Välj din plan inne i appen",
    },
    footer: { legal: "Juridik", company: "Företag", privacy: "Integritetspolicy", terms: "Användarvillkor", contact: "Kontakt", rights: "Alla rättigheter förbehållna." },
  },

  en: {
    seo: {
      title: "Swedish Driving Theory Test in English – Licence B | Teorikörkort",
      description: "Prepare for the Swedish driving theory test (licence B) in English. Practise 500+ exam-style questions, every road sign and the full theory on your phone.",
    },
    nav: { home: "Home", features: "Features", faq: "FAQ", contact: "Contact" },
    a11y: { menu: "Menu", lang: "Change language", appStore: "Download on the App Store", googlePlay: "Get it on Google Play" },
    hero: {
      titlePre: "Pass your ", titleEm: "theory test.", titlePost: "First try.",
      lead: "Teorikörkort is the app that takes you through the theory test. Read the theory, practise exam-style questions, and look up every road sign — in Swedish, English, Ukrainian, or Russian.",
      altHome: "Teorikörkort — the app's home screen",
      altQuestion: "Teorikörkort — a practice question with answer options",
      social: "Loved by our users",
      buyLabel: "Buy right now", buyNote: "Unlock full access inside the app",
    },
    how: {
      tag: "How it works", titlePre: "Three steps to ", titleEm: "passing", titlePost: " your theory test",
      lead: "Set up in 30 seconds. Start studying today.",
      steps: [
        { title: "Practise real exam questions", description: "Answer exam-style multiple-choice questions with an image and options — just like the real test. You see the correct answer and an explanation right away.", alt: "Teorikörkort — a practice question with an image and answer options" },
        { title: "Read the theory", description: "Work through the traffic rules chapter by chapter, with images, examples, and a complete catalogue of Swedish road signs.", alt: "Teorikörkort — a chapter in the theory section" },
        { title: "Practise until you're ready", description: "Run mock exams and see results by topic area. The stats show exactly what to review.", alt: "Teorikörkort — the results screen after a mock exam" },
      ],
    },
    features: {
      tag: "More than questions", titlePre: "Built to get you ", titleEm: "ready",
      items: [
        { name: "Every road sign", description: "Browse and search every Swedish road sign, grouped into categories with names and explanations." },
        { name: "Exam-style questions", description: "Questions follow the real test's format and weighting, with an explanation for every answer." },
        { name: "Four languages", description: "Swedish, English, Ukrainian, and Russian — the whole app, not just the menus. Switch anytime." },
        { name: "Track your progress", description: "Results by topic and full exam history show what to review before test day." },
      ],
    },
    faq: {
      tag: "Got questions?", heading: "FAQ",
      items: [
        { question: "How many questions are in the app?", answer: "Over 500 practice questions covering all topic areas of the theory test. The content is updated regularly." },
        { question: "Are the questions like the real test?", answer: "Yes. The questions use the same multiple-choice format as Trafikverket's theory test, and the mock exams follow the test's weighting across topic areas. Every answer comes with an explanation so you understand why it's correct." },
        { question: "Which languages are supported?", answer: "Swedish, English, Ukrainian, and Russian. All content is translated — theory chapters, questions, and road signs — and you can switch language anytime inside the app." },
        { question: "Are all road signs included?", answer: "Yes. The app has a searchable catalogue of Swedish road signs, grouped into categories such as warning, prohibitory, and mandatory signs, with a name and explanation for each." },
        { question: "Can I try the app before paying?", answer: "Yes. You can take a short practice quiz without an account to see how the app works. Full access to the theory and all questions is unlocked with a subscription." },
        { question: "Which licence category does the content cover?", answer: "The content focuses on the theory test for category B (passenger car) and is based on current Swedish traffic rules." },
      ],
    },
    reviews: {
      tag: "Reviews", heading: "Loved by learners",
      items: [
        { text: "Clear and never overwhelming. The explanations for wrong answers are spot on, and the theory covers everything with examples. Getting my körkort in a couple of months!", label: "Verified user" },
        { text: "Really helpful for the Swedish driving test. Love that the rules are translated into Ukrainian and Russian, plus the exam mode feels just like the real thing. It keeps getting better too. I'd definitely recommend it.", label: "Verified user" },
        { text: "Tried a bunch of theory apps and this is easily the best. Simple, everything just makes sense. Passed no problem.", label: "Passed first try" },
      ],
    },
    pricing: {
      tag: "Pricing", heading: "Simple pricing", lead: "One purchase, made inside the app. Pick the plan that fits you.",
      plans: [
        { name: "1 month", period: "per month", note: "Great to get started", badge: "" },
        { name: "3 months", period: "for 3 months", note: "Our most popular plan", badge: "Popular" },
        { name: "Lifetime", period: "one-time payment", note: "Pay once, yours forever", badge: "Best value" },
      ],
      buyLabel: "Buy right now", buyNote: "Choose your plan inside the app",
    },
    footer: { legal: "Legal", company: "Company", privacy: "Privacy Policy", terms: "Terms of Service", contact: "Contact", rights: "All rights reserved." },
  },

  uk: {
    seo: {
      title: "Теорія водіння у Швеції українською – іспит B | Teorikörkort",
      description: "Готуйся до теоретичного іспиту на права категорії B у Швеції українською. Понад 500 питань як на іспиті, усі дорожні знаки й теорія в телефоні.",
    },
    nav: { home: "Головна", features: "Функції", faq: "Поширені запитання", contact: "Контакти" },
    a11y: { menu: "Меню", lang: "Змінити мову", appStore: "Завантажити в App Store", googlePlay: "Завантажити в Google Play" },
    hero: {
      titlePre: "Склади ", titleEm: "теоретичний іспит.", titlePost: "З першого разу.",
      lead: "Teorikörkort — застосунок, який проведе тебе через теоретичний іспит. Читай теорію, тренуйся на екзаменаційних питаннях і шукай будь-який дорожній знак — шведською, англійською, українською чи російською.",
      altHome: "Teorikörkort — головний екран застосунку",
      altQuestion: "Teorikörkort — тренувальне питання з варіантами відповідей",
      social: "Обожнюють наші користувачі",
      buyLabel: "Купуй зараз", buyNote: "Повний доступ відкривається в застосунку",
    },
    how: {
      tag: "Як це працює", titlePre: "Три кроки до ", titleEm: "успішного", titlePost: " теоретичного іспиту",
      lead: "Налаштування за 30 секунд. Починай вчитися вже сьогодні.",
      steps: [
        { title: "Тренуйся на справжніх питаннях", description: "Відповідай на тестові питання із зображенням і варіантами — як на справжньому іспиті. Одразу бачиш правильну відповідь і пояснення.", alt: "Teorikörkort — тренувальне питання із зображенням і варіантами відповідей" },
        { title: "Читай теорію", description: "Проходь правила дорожнього руху розділ за розділом — з ілюстраціями, прикладами та повним каталогом шведських дорожніх знаків.", alt: "Teorikörkort — розділ теоретичної частини" },
        { title: "Тренуйся, поки не будеш готовий", description: "Проходь пробні іспити й дивись результати за темами. Статистика показує, що саме варто повторити.", alt: "Teorikörkort — екран результатів після пробного іспиту" },
      ],
    },
    features: {
      tag: "Більше ніж питання", titlePre: "Створено, щоб зробити тебе ", titleEm: "готовим",
      items: [
        { name: "Усі дорожні знаки", description: "Переглядай і шукай усі шведські дорожні знаки, поділені на категорії, з назвами та поясненнями." },
        { name: "Питання як на іспиті", description: "Питання відповідають формату та ваговим коефіцієнтам іспиту, з поясненням до кожної відповіді." },
        { name: "Чотири мови", description: "Шведська, англійська, українська та російська — весь застосунок, а не лише меню. Змінюй мову будь-коли." },
        { name: "Стеж за прогресом", description: "Результати за темами та повна історія іспитів показують, що повторити перед іспитом." },
      ],
    },
    faq: {
      tag: "Є запитання?", heading: "Поширені запитання",
      items: [
        { question: "Скільки питань у застосунку?", answer: "Понад 500 тренувальних питань, що охоплюють усі теми теоретичного іспиту. Контент регулярно оновлюється." },
        { question: "Чи схожі питання на справжній іспит?", answer: "Так. Питання мають той самий формат із варіантами відповідей, що й іспит Trafikverket, а пробні іспити відповідають розподілу тем справжнього іспиту. До кожної відповіді є пояснення, щоб ти розумів, чому вона правильна." },
        { question: "Які мови підтримуються?", answer: "Шведська, англійська, українська та російська. Увесь контент перекладено — теоретичні розділи, питання та дорожні знаки — і ти можеш змінити мову будь-коли в застосунку." },
        { question: "Чи є всі дорожні знаки?", answer: "Так. У застосунку є каталог шведських дорожніх знаків із пошуком, поділений на категорії — попереджувальні, заборонні та наказові знаки — з назвою та поясненням до кожного." },
        { question: "Чи можна спробувати застосунок перед оплатою?", answer: "Так. Можна пройти коротке пробне тестування без облікового запису, щоб побачити, як працює застосунок. Повний доступ до теорії та всіх питань відкривається за підпискою." },
        { question: "Для якої категорії призначено контент?", answer: "Контент орієнтований на теоретичний іспит для категорії B (легковий автомобіль) і базується на чинних шведських правилах дорожнього руху." },
      ],
    },
    reviews: {
      tag: "Відгуки", heading: "Обожнюють учні",
      items: [
        { text: "Зручно й зрозуміло, нічого зайвого. Чудові пояснення до помилок, а теорія розписана до дрібниць з прикладами. За пару місяців планую отримати körkort!", label: "Склав з першого разу" },
        { text: "Дуже корисний для підготовки до іспиту на права у Швеції. Подобається, що правила перекладені українською та російською, а режим тестування як справжній іспит. До того ж застосунок постійно вдосконалюється. Однозначно рекомендую!", label: "Перевірений користувач" },
        { text: "Пробував купу застосунків для теорії, і цей найкращий. Простий, усе зрозуміло. Склав без проблем.", label: "Склав з першого разу" },
      ],
    },
    pricing: {
      tag: "Ціни", heading: "Прості ціни", lead: "Одна покупка — прямо в застосунку. Обери план, який тобі підходить.",
      plans: [
        { name: "1 місяць", period: "на місяць", note: "Чудово для початку", badge: "" },
        { name: "3 місяці", period: "за 3 місяці", note: "Найпопулярніший план", badge: "Популярний" },
        { name: "Назавжди", period: "разова оплата", note: "Заплати один раз — назавжди твоє", badge: "Найвигідніше" },
      ],
      buyLabel: "Купуй зараз", buyNote: "Обери свій план у застосунку",
    },
    footer: { legal: "Правова інформація", company: "Компанія", privacy: "Політика конфіденційності", terms: "Умови користування", contact: "Контакти", rights: "Усі права захищено." },
  },

  ru: {
    seo: {
      title: "Теория вождения в Швеции на русском – экзамен B | Teorikörkort",
      description: "Готовься к теоретическому экзамену на права категории B в Швеции на русском. Более 500 вопросов как на экзамене, все дорожные знаки и теория в телефоне.",
    },
    nav: { home: "Главная", features: "Функции", faq: "Частые вопросы", contact: "Контакты" },
    a11y: { menu: "Меню", lang: "Сменить язык", appStore: "Загрузить в App Store", googlePlay: "Доступно в Google Play" },
    hero: {
      titlePre: "Сдай ", titleEm: "теоретический экзамен.", titlePost: "С первого раза.",
      lead: "Teorikörkort — приложение, которое проведёт тебя через теоретический экзамен. Читай теорию, тренируйся на экзаменационных вопросах и находи любой дорожный знак — на шведском, английском, украинском или русском.",
      altHome: "Teorikörkort — главный экран приложения",
      altQuestion: "Teorikörkort — тренировочный вопрос с вариантами ответов",
      social: "Нас любят пользователи",
      buyLabel: "Купи сейчас", buyNote: "Полный доступ открывается в приложении",
    },
    how: {
      tag: "Как это работает", titlePre: "Три шага к ", titleEm: "сданному", titlePost: " экзамену",
      lead: "Настройка за 30 секунд. Начни учиться уже сегодня.",
      steps: [
        { title: "Тренируйся на реальных вопросах", description: "Отвечай на тестовые вопросы с изображением и вариантами — как на настоящем экзамене. Сразу видишь правильный ответ и объяснение.", alt: "Teorikörkort — тренировочный вопрос с изображением и вариантами ответов" },
        { title: "Читай теорию", description: "Изучай правила дорожного движения глава за главой — с иллюстрациями, примерами и полным каталогом шведских дорожных знаков.", alt: "Teorikörkort — глава теоретической части" },
        { title: "Тренируйся, пока не будешь готов", description: "Проходи пробные экзамены и смотри результаты по темам. Статистика показывает, что именно нужно повторить.", alt: "Teorikörkort — экран результатов после пробного экзамена" },
      ],
    },
    features: {
      tag: "Больше, чем вопросы", titlePre: "Создано, чтобы сделать тебя ", titleEm: "готовым",
      items: [
        { name: "Все дорожные знаки", description: "Просматривай и ищи все шведские дорожные знаки по категориям — с названиями и пояснениями." },
        { name: "Вопросы как на экзамене", description: "Вопросы соответствуют формату и весам реального экзамена, с объяснением к каждому ответу." },
        { name: "Четыре языка", description: "Шведский, английский, украинский и русский — всё приложение, а не только меню. Меняй язык в любой момент." },
        { name: "Следи за прогрессом", description: "Результаты по темам и полная история экзаменов показывают, что повторить перед экзаменом." },
      ],
    },
    faq: {
      tag: "Есть вопросы?", heading: "Частые вопросы",
      items: [
        { question: "Сколько вопросов в приложении?", answer: "Более 500 тренировочных вопросов по всем темам теоретического экзамена. Контент регулярно обновляется." },
        { question: "Похожи ли вопросы на настоящий экзамен?", answer: "Да. Вопросы в том же формате с вариантами ответов, что и экзамен Trafikverket, а пробные экзамены повторяют распределение тем реального экзамена. К каждому ответу есть объяснение, чтобы ты понимал, почему он верный." },
        { question: "Какие языки поддерживаются?", answer: "Шведский, английский, украинский и русский. Весь контент переведён — теоретические главы, вопросы и дорожные знаки — и язык можно сменить в любой момент в приложении." },
        { question: "Есть ли все дорожные знаки?", answer: "Да. В приложении есть каталог шведских дорожных знаков с поиском, разделённый на категории — предупреждающие, запрещающие и предписывающие знаки — с названием и пояснением к каждому." },
        { question: "Можно ли попробовать приложение до оплаты?", answer: "Да. Можно пройти короткий пробный тест без аккаунта, чтобы увидеть, как работает приложение. Полный доступ к теории и всем вопросам открывается по подписке." },
        { question: "Для какой категории предназначен контент?", answer: "Контент ориентирован на теоретический экзамен для категории B (легковой автомобиль) и основан на действующих шведских правилах дорожного движения." },
      ],
    },
    reviews: {
      tag: "Отзывы", heading: "Нас выбирают ученики",
      items: [
        { text: "Удобно и понятно, ничего лишнего. Отличные пояснения к ошибкам, а теория расписана до мелочей с примерами. Через пару месяцев планирую получить körkort!", label: "Проверенный пользователь" },
        { text: "Очень полезен для подготовки к экзамену на права в Швеции. Нравится, что правила переведены на украинский и русский, а режим тестирования как настоящий экзамен. К тому же приложение постоянно улучшается. Однозначно рекомендую!", label: "Проверенный пользователь" },
        { text: "Пробовал кучу приложений для теории, это лучшее. Простое, всё понятно. Сдал без проблем.", label: "Сдал с первого раза" },
      ],
    },
    pricing: {
      tag: "Цены", heading: "Простые цены", lead: "Одна покупка — прямо в приложении. Выбери подходящий план.",
      plans: [
        { name: "1 месяц", period: "в месяц", note: "Отлично для старта", badge: "" },
        { name: "3 месяца", period: "за 3 месяца", note: "Самый популярный план", badge: "Популярный" },
        { name: "Навсегда", period: "разовый платёж", note: "Заплати один раз — навсегда твоё", badge: "Выгоднее всего" },
      ],
      buyLabel: "Купи сейчас", buyNote: "Выбери свой план в приложении",
    },
    footer: { legal: "Правовая информация", company: "Компания", privacy: "Политика конфиденциальности", terms: "Условия использования", contact: "Контакты", rights: "Все права защищены." },
  },
};

/* SEO title + description for a language (used by scripts/prerender.js). */
export function landingSeo(lang) {
  return (TR[lang] || TR[DEFAULT_LANG]).seo;
}

/* Plans shown in the pricing section, for structured data (scripts/prerender.js). */
export function landingPlans(lang) {
  return (TR[lang] || TR[DEFAULT_LANG]).pricing.plans.map((p, i) => ({ name: p.name, price: PLAN_META[i].amount }));
}

/* -------------------------------------------------------------------------- */
/* Breakpoint hook                                                             */
/* -------------------------------------------------------------------------- */

function bpFor(w) {
  if (w >= 1024) return "lg";
  if (w >= 768) return "md";
  if (w >= 640) return "sm";
  return "base";
}
/* The static HTML is prerendered at SSR_BP (mobile first), so the first client
   render uses it too and hydration matches; the layout effect then switches to
   the real breakpoint before the browser paints. */
export const SSR_BP = "base";
function useBreakpoint() {
  const [bp, setBp] = useState(SSR_BP);
  useLayoutEffect(() => {
    const onResize = () => setBp(bpFor(window.innerWidth));
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return bp;
}

function HoverLink({ base, hover, children, ...rest }) {
  const [h, setH] = useState(false);
  return (
    <a
      {...rest}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{ ...base, ...(h ? hover : null) }}
    >
      {children}
    </a>
  );
}

/* -------------------------------------------------------------------------- */
/* Icons                                                                       */
/* -------------------------------------------------------------------------- */

const outline = {
  xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24",
  strokeWidth: 1.5, stroke: "currentColor", "aria-hidden": "true",
};

const Bars3Icon = (p) => (<svg {...outline} {...p}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /></svg>);
const XMarkIcon = (p) => (<svg {...outline} {...p}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>);
const ChevronDownIcon = (p) => (<svg {...outline} {...p}><path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>);
const CheckIcon = (p) => (<svg {...outline} {...p}><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>);
const GlobeIcon = (p) => (<svg {...outline} {...p}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0 0a8.949 8.949 0 0 0 4.951-1.488A3.987 3.987 0 0 0 13 16h-2a2 2 0 0 1-2-2 2 2 0 0 0-2-2H3.055M15 3.5a8.988 8.988 0 0 1 3.756 4M12 6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a1 1 0 0 0 1 1 2 2 0 0 1 2 2" /></svg>);
const SignpostIcon = (p) => (<svg {...outline} {...p}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m0 0h6.75l1.5 2.25-1.5 2.25H12m0-4.5H5.25l-1.5 2.25 1.5 2.25H12m0 0v3m0 0h5.25l1.5 2.25-1.5 2.25H6.75L5.25 15l1.5-2.25H12Zm0 6.75V21" /></svg>);
const ClipboardCheckIcon = (p) => (<svg {...outline} {...p}><path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75" /></svg>);
const LanguageIcon = (p) => (<svg {...outline} {...p}><path strokeLinecap="round" strokeLinejoin="round" d="m10.5 21 5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 0 1 6-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 0 1-3.827-5.802" /></svg>);
const ChartBarIcon = (p) => (<svg {...outline} {...p}><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" /></svg>);

const StarIcon = (p) => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}><path d="M12 2.25l2.955 5.988 6.607.96-4.781 4.66 1.128 6.582L12 17.29l-5.909 3.106 1.128-6.583-4.781-4.66 6.607-.959L12 2.25z" /></svg>);

const FEATURE_ICONS = [SignpostIcon, ClipboardCheckIcon, LanguageIcon, ChartBarIcon];

/* -------------------------------------------------------------------------- */
/* Style factory                                                               */
/* -------------------------------------------------------------------------- */

function makeStyles(bp) {
  const smUp = bp === "sm" || bp === "md" || bp === "lg";
  const mdUp = bp === "md" || bp === "lg";
  const lgUp = bp === "lg";

  const badgeLinkBase = { display: "inline-block", transition: "opacity .15s cubic-bezier(.4,0,.2,1)" };
  const badgeLinkHover = { opacity: 0.8 };

  return {
    page: { minHeight: "100vh", background: C.white, fontFamily: FONT, color: C.zinc900, lineHeight: 1.5 },
    sections: {},
    container: { maxWidth: "1300px", marginInline: "auto", paddingInline: lgUp ? "3rem" : "1.5rem" },
    section: {},
    sectionHead: { maxWidth: "42rem", marginInline: "auto", textAlign: "center" },
    tag: { display: "inline-block", borderRadius: "999px", background: C.zinc100, padding: ".25rem .75rem", fontSize: ".75rem", lineHeight: 1.3333, fontWeight: 600, color: C.zinc700, boxShadow: `0 0 0 1px ${C.zinc200}` },
    h2: { marginTop: "1.5rem", fontSize: smUp ? "3.25rem" : "2.5rem", lineHeight: smUp ? 1.08 : 1.12, fontWeight: 800, letterSpacing: "-.03em", color: C.zinc900 },
    sectionLead: { marginTop: "1.25rem", fontSize: "1.125rem", lineHeight: 1.625, color: C.zinc600 },
    em: { fontStyle: "normal" },

    badgeLinkBase, badgeLinkHover,
    badges: { marginLeft: smUp ? "-.75rem" : "-.5rem", display: "flex", flexDirection: smUp ? "row" : "column", alignItems: smUp ? "center" : "flex-start", gap: smUp ? ".5rem" : ".25rem", flexWrap: smUp ? "wrap" : "nowrap", paddingTop: ".5rem" },
    badgesCenter: { display: "flex", flexDirection: smUp ? "row" : "column", alignItems: "center", justifyContent: "center", gap: smUp ? ".5rem" : ".25rem", flexWrap: "wrap" },
    badgeApple: { height: "56px", width: "auto" },
    badgeGoogle: { height: "82px", width: "auto", objectFit: "contain", marginBlock: "-.75rem", marginInline: "-.75rem" },

    /* navbar */
    nav: { width: "100%", padding: `1rem ${lgUp ? "3rem" : "1.5rem"}` },
    navGrid: { maxWidth: "1300px", marginInline: "auto", display: "grid", gridTemplateColumns: mdUp ? "auto minmax(0,1fr) auto" : "1fr auto 1fr", alignItems: "center", gap: "1.5rem" },
    navLogo: { justifySelf: "start" },
    navLogoImg: { height: "5rem", width: "auto" },
    navLinks: { display: mdUp ? "flex" : "none", alignItems: "center", justifySelf: "center", gap: lgUp ? "1.75rem" : "1.25rem" },
    navLink: { color: C.gray700, fontSize: ".875rem", lineHeight: 1.4286, fontWeight: 500, whiteSpace: "nowrap", transition: "color .15s cubic-bezier(.4,0,.2,1)" },
    navLinkHover: { color: C.gray900 },
    navRight: { display: "flex", alignItems: "center", gap: ".6rem", justifySelf: "end", gridColumnStart: 3 },
    // Store badges in the navbar only on wide screens; on tablets the five nav links need the room.
    navStores: { display: lgUp ? "flex" : "none", alignItems: "center", gap: ".75rem" },
    navApple: { height: "2.5rem", width: "auto" },
    navGoogle: { height: "58px", width: "auto", marginBlock: "-.5rem" },
    burger: { display: mdUp ? "none" : "inline-flex", padding: ".5rem", border: "0", background: "transparent", cursor: "pointer" },
    burgerIcon: { width: "1.5rem", height: "1.5rem", color: C.gray900 },
    mobileMenu: { marginTop: "1rem", paddingBottom: "1rem", borderTop: `1px solid ${C.gray200}` },
    mobileInner: { display: "flex", flexDirection: "column", gap: "1rem", paddingTop: "1rem" },
    mobileLink: { color: C.gray700, fontSize: "1rem", lineHeight: 1.5, paddingInline: "1rem", transition: "color .15s cubic-bezier(.4,0,.2,1)" },
    mobileStores: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: ".75rem", paddingInline: "1rem", paddingTop: ".5rem" },

    /* language switcher */
    langWrap: { position: "relative" },
    langBtn: { display: "inline-flex", alignItems: "center", gap: ".4rem", padding: ".5rem .6rem", borderRadius: ".7rem", border: `1px solid ${C.zinc200}`, background: C.white, color: C.zinc700, fontSize: ".8rem", fontWeight: 600, lineHeight: 1, cursor: "pointer", transition: "background-color .15s" },
    langBtnGlobe: { width: "1.1rem", height: "1.1rem", color: C.zinc500 },
    langBtnChev: { width: ".8rem", height: ".8rem", color: C.zinc500, transition: "transform .2s" },
    langMenu: { position: "absolute", top: "calc(100% + .45rem)", right: 0, minWidth: "11.5rem", background: C.white, border: `1px solid ${C.zinc200}`, borderRadius: ".85rem", boxShadow: "0 14px 34px -10px rgba(0,0,0,.22)", padding: ".35rem", zIndex: 60 },
    langItem: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".75rem", width: "100%", padding: ".55rem .65rem", borderRadius: ".55rem", fontSize: ".9rem", fontWeight: 500, color: C.zinc700, background: "transparent", cursor: "pointer", textAlign: "left", transition: "background-color .12s" },
    langItemActive: { background: C.zinc100, color: C.zinc900, fontWeight: 700 },
    langCheck: { width: "1rem", height: "1rem", color: C.zinc900, flexShrink: 0 },
    langCode: { fontSize: ".72rem", fontWeight: 700, color: C.zinc500, letterSpacing: ".03em" },

    /* hero */
    hero: { position: "relative", marginBottom: lgUp ? 0 : "4rem", overflowX: "clip" },
    heroGlow: { pointerEvents: "none", position: "absolute", insetInline: 0, top: 0, zIndex: -10, height: "700px", background: "radial-gradient(60% 60% at 70% 10%, rgba(0,0,0,.04) 0%, rgba(255,255,255,0) 70%)" },
    heroContainer: { marginInline: "auto", maxWidth: "1300px", padding: lgUp ? "0 3rem 7rem" : ".5rem 1.5rem 2rem" },
    heroGrid: { display: "grid", alignItems: "center", gridTemplateColumns: lgUp ? "repeat(2, minmax(0,1fr))" : "minmax(0,1fr)", gap: lgUp ? "4rem" : "3rem" },
    heroCopy: { textAlign: "left" },
    heroTitle: { marginTop: "1.5rem", fontSize: lgUp ? "4.5rem" : smUp ? "3.9rem" : "3.15rem", fontWeight: 800, letterSpacing: "-.03em", color: C.zinc900, lineHeight: 1.05 },
    heroLead: { marginTop: "1.5rem", maxWidth: "32rem", fontSize: smUp ? "1.25rem" : "1.125rem", lineHeight: 1.625, color: C.zinc600 },

    /* hero social proof */
    heroSocial: { display: "flex", alignItems: "center", gap: ".85rem" },
    heroSocialAvatars: { display: "flex", alignItems: "center" },
    heroSocialAvatar: { width: "2.25rem", height: "2.25rem", borderRadius: "999px", border: `2px solid ${C.white}`, objectFit: "cover", background: C.zinc100, boxShadow: `0 0 0 1px ${C.zinc200}` },
    heroSocialTextWrap: { display: "flex", flexDirection: "column", gap: ".15rem" },
    heroSocialStars: { display: "flex", gap: ".1rem" },
    heroSocialStar: { width: ".95rem", height: ".95rem", color: C.amber },
    heroSocialText: { fontSize: ".85rem", fontWeight: 600, color: C.zinc600 },

    heroCta: { marginTop: "2rem", display: "flex" },
    heroBuy: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: ".85rem" },
    buyBadge: { display: "inline-flex", alignItems: "center", gap: ".5rem", borderRadius: "999px", background: C.amber, color: C.zinc900, padding: ".55rem 1.05rem", fontSize: "1rem", fontWeight: 700, letterSpacing: "-.01em" },
    heroBuyNote: { fontSize: ".85rem", color: C.zinc500 },
    heroPracticeLink: { fontSize: ".95rem", fontWeight: 700, color: C.zinc900, textDecoration: "underline", textUnderlineOffset: "3px" },
    heroMediaCol: { position: "relative", display: "flex", justifyContent: lgUp ? "flex-end" : "center" },
    heroBlur: { position: "absolute", inset: 0, zIndex: -10, borderRadius: "999px", background: "rgba(228,228,231,.5)", filter: "blur(64px)" },
    heroStage: { position: "relative", height: smUp ? "660px" : "440px", width: "100%", maxWidth: "560px" },
    phoneBase: { position: "absolute", top: "50%", width: smUp ? "310px" : "190px", filter: "drop-shadow(0 25px 25px rgba(0,0,0,.15))" },
    phoneLeft: { left: 0, marginTop: smUp ? "4rem" : "2.5rem", transform: "translateY(-50%) rotate(-8deg)" },
    phoneRight: { right: 0, transform: "translateY(-50%) rotate(6deg)" },

    /* steps */
    steps: { marginTop: lgUp ? "5rem" : "4rem", display: "grid", gridTemplateColumns: lgUp ? "repeat(3, minmax(0,1fr))" : "minmax(0,1fr)", gap: lgUp ? "2rem" : "2.5rem" },
    step: { display: "flex", flexDirection: "column" },
    stepMedia: { position: "relative", aspectRatio: "4 / 5", width: "100%", overflow: "hidden", borderRadius: "1.5rem", background: C.zinc50, boxShadow: `0 0 0 1px ${C.zinc100}` },
    stepNum: { position: "absolute", left: "1.25rem", top: "1.25rem", zIndex: 10, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "999px", background: C.zinc900, padding: ".25rem .625rem", fontSize: ".75rem", lineHeight: 1.3333, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: C.white },
    stepImg: { position: "absolute", inset: 0, height: "100%", width: "100%", objectFit: "contain", padding: "1.5rem" },
    stepTitle: { marginTop: "1.5rem", fontSize: "1.625rem", lineHeight: 1.25, fontWeight: 700, letterSpacing: "-.02em", color: C.zinc900 },
    stepDesc: { marginTop: ".75rem", fontSize: "1rem", lineHeight: 1.625, color: C.zinc600 },

    /* features */
    features: { marginTop: lgUp ? "5rem" : "4rem", display: "grid", gridTemplateColumns: lgUp ? "repeat(4, minmax(0,1fr))" : smUp ? "repeat(2, minmax(0,1fr))" : "minmax(0,1fr)", columnGap: "2rem", rowGap: "2.5rem" },
    feature: { display: "flex", flexDirection: "column", alignItems: "flex-start" },
    featureIconBox: { display: "flex", height: "2.75rem", width: "2.75rem", alignItems: "center", justifyContent: "center", borderRadius: ".75rem", background: C.zinc100, boxShadow: `0 0 0 1px ${C.zinc200}` },
    featureIcon: { height: "1.25rem", width: "1.25rem", color: C.zinc700 },
    featureTitle: { marginTop: "1.25rem", fontSize: "1.25rem", lineHeight: 1.4, fontWeight: 700, letterSpacing: "-.01em", color: C.zinc900 },
    featureDesc: { marginTop: ".5rem", fontSize: ".875rem", lineHeight: 1.625, color: C.zinc600 },

    /* reviews */
    reviews: { marginTop: lgUp ? "5rem" : "4rem", display: "grid", gridTemplateColumns: lgUp ? "repeat(3, minmax(0,1fr))" : smUp ? "repeat(2, minmax(0,1fr))" : "minmax(0,1fr)", gap: "1.5rem" },
    reviewCard: { display: "flex", flexDirection: "column", height: "100%", padding: "1.75rem", borderRadius: "1.25rem", background: C.white, boxShadow: `0 0 0 1px ${C.zinc200}` },
    reviewStars: { display: "flex", gap: ".15rem", marginBottom: "1rem" },
    reviewStar: { width: "1.05rem", height: "1.05rem", color: C.amber },
    reviewText: { fontSize: "1rem", lineHeight: 1.625, color: C.zinc700, flexGrow: 1 },
    reviewer: { display: "flex", alignItems: "center", gap: ".75rem", marginTop: "1.5rem" },
    reviewerAvatar: { width: "2.75rem", height: "2.75rem", borderRadius: "999px", objectFit: "cover", background: C.zinc100, boxShadow: `0 0 0 1px ${C.zinc200}` },
    reviewerName: { fontSize: ".95rem", fontWeight: 700, color: C.zinc900, lineHeight: 1.3 },
    reviewerLabel: { fontSize: ".8rem", color: C.zinc500, lineHeight: 1.3 },

    /* pricing */
    pricing: { maxWidth: "64rem", marginInline: "auto", marginTop: lgUp ? "4rem" : "3rem", display: "grid", gridTemplateColumns: mdUp ? "repeat(3, minmax(0,1fr))" : "minmax(0,1fr)", gap: "1.5rem", alignItems: "stretch" },
    priceCard: { position: "relative", display: "flex", flexDirection: "column", padding: "2rem", borderRadius: "1.5rem", background: C.white, boxShadow: `0 0 0 1px ${C.zinc200}` },
    priceCardFeatured: { background: C.zinc900, boxShadow: "none" },
    priceBadge: { position: "absolute", top: "1.25rem", right: "1.25rem", borderRadius: "999px", background: C.zinc100, padding: ".25rem .625rem", fontSize: ".7rem", fontWeight: 700, color: C.zinc700, boxShadow: `0 0 0 1px ${C.zinc200}` },
    priceBadgeFeatured: { position: "absolute", top: "1.25rem", right: "1.25rem", borderRadius: "999px", background: C.amber, padding: ".25rem .625rem", fontSize: ".7rem", fontWeight: 700, color: C.zinc900 },
    priceName: { fontSize: "1.125rem", fontWeight: 700, color: C.zinc900 },
    priceNameFeatured: { fontSize: "1.125rem", fontWeight: 700, color: C.white },
    priceAmountRow: { marginTop: "1rem", display: "flex", alignItems: "baseline" },
    priceAmount: { fontSize: "2.75rem", fontWeight: 800, letterSpacing: "-.02em", lineHeight: 1, color: C.zinc900 },
    priceAmountFeatured: { fontSize: "2.75rem", fontWeight: 800, letterSpacing: "-.02em", lineHeight: 1, color: C.white },
    pricePeriod: { marginTop: ".6rem", fontSize: ".9rem", color: C.zinc500 },
    pricePeriodFeatured: { marginTop: ".6rem", fontSize: ".9rem", color: C.zinc400 },
    priceNote: { marginTop: "1.25rem", fontSize: ".9rem", lineHeight: 1.5, color: C.zinc600 },
    priceNoteFeatured: { marginTop: "1.25rem", fontSize: ".9rem", lineHeight: 1.5, color: C.zinc300 },
    priceBuy: { maxWidth: "64rem", marginInline: "auto", marginTop: "2.5rem", display: "flex", flexDirection: "column", alignItems: "center", gap: ".85rem", textAlign: "center" },
    priceBuyNote: { fontSize: ".9rem", color: C.zinc500 },

    /* faq */
    faqContainer: { maxWidth: "48rem", marginInline: "auto", paddingInline: lgUp ? "2rem" : "1.5rem" },
    faqHead: { textAlign: "center" },
    faqList: { marginTop: "3.5rem", borderTop: `1px solid ${C.zinc200}`, borderBottom: `1px solid ${C.zinc200}` },
    faqButton: { display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between", gap: "1.5rem", padding: "1.25rem 1rem", textAlign: "left", border: "0", cursor: "pointer", transition: "background-color .15s cubic-bezier(.4,0,.2,1)" },
    faqQuestion: { fontSize: smUp ? "1.1875rem" : "1.0625rem", lineHeight: 1.5, fontWeight: 700, color: C.zinc900 },
    faqChevron: { height: "1.25rem", width: "1.25rem", flexShrink: 0, transition: "transform .3s cubic-bezier(0,0,.2,1), color .15s" },
    faqAnswer: (open) => ({ overflow: "hidden", maxHeight: open ? "500px" : "0", opacity: open ? 1 : 0, transition: "max-height .25s ease-out, opacity .2s ease-out" }),
    faqAnswerText: { padding: ".75rem 1rem 1.75rem", fontSize: "1rem", lineHeight: 1.625, color: C.zinc600 },

    /* footer */
    footer: { borderTop: `1px solid ${C.gray200}`, paddingBlock: smUp ? "4rem" : "3rem" },
    footerContainer: { maxWidth: "80rem", marginInline: "auto", paddingInline: lgUp ? "3rem" : "1.5rem" },
    footerTop: { display: "grid", gap: smUp ? "3rem" : "2rem" },
    footerBrand: { display: "flex", flexDirection: "column", gap: smUp ? "1.5rem" : "1rem", alignItems: "flex-start" },
    footerLogo: { height: smUp ? "3rem" : "2.5rem", width: "auto" },
    footerCols: { display: "grid", gridTemplateColumns: mdUp ? "repeat(3, minmax(0,1fr))" : "repeat(2, minmax(0,1fr))", gap: smUp ? "2rem" : "1.5rem" },
    footerCol: { display: "flex", flexDirection: "column", gap: smUp ? "1rem" : ".75rem" },
    footerColTitle: { fontWeight: 700, color: C.gray900, fontSize: smUp ? "1rem" : ".875rem", lineHeight: 1.4286 },
    footerList: { display: "flex", flexDirection: "column", gap: ".5rem", listStyle: "none", margin: 0, padding: 0 },
    footerLink: { color: C.gray600, fontSize: smUp ? ".875rem" : ".75rem", lineHeight: 1.3333, transition: "color .15s cubic-bezier(.4,0,.2,1)" },
    footerLinkHover: { color: C.gray900 },
    footerBottom: { marginTop: "3rem", paddingTop: "2rem", borderTop: `1px solid ${C.gray200}` },
    footerCopy: { textAlign: "center", fontSize: ".875rem", lineHeight: 1.4286, color: C.gray500 },
  };
}

/* -------------------------------------------------------------------------- */
/* Reusable bits                                                               */
/* -------------------------------------------------------------------------- */

function StoreBadges({ s, t, center, eager }) {
  const loading = eager ? "eager" : "lazy";
  return (
    <div style={center ? s.badgesCenter : s.badges}>
      <HoverLink href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" base={s.badgeLinkBase} hover={s.badgeLinkHover}>
        <img src={IMG.apple} alt={t.a11y.appStore} width="120" height="40" loading={loading} decoding="async" style={s.badgeApple} />
      </HoverLink>
      <HoverLink href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" base={s.badgeLinkBase} hover={s.badgeLinkHover}>
        <img src={IMG.google} alt={t.a11y.googlePlay} width="646" height="250" loading={loading} decoding="async" style={s.badgeGoogle} />
      </HoverLink>
    </div>
  );
}

function Stars({ count = 5, style, starStyle }) {
  return (
    <div style={style} role="img" aria-label={`${count} / 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <StarIcon key={i} style={starStyle} />
      ))}
    </div>
  );
}

function LangSwitcher({ s, t, lang, hrefFor }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const wrapRef = useRef(null);
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div style={s.langWrap} ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-label={t.a11y.lang}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ ...s.langBtn, backgroundColor: hover ? C.zinc50 : C.white }}
      >
        <GlobeIcon style={s.langBtnGlobe} />
        <span>{current.short}</span>
        <ChevronDownIcon style={{ ...s.langBtnChev, transform: open ? "rotate(180deg)" : "none" }} />
      </button>

      {open && (
        <div style={s.langMenu} role="listbox" aria-label={t.a11y.lang} data-track-placement="language_menu">
          {LANGS.map((l) => {
            const active = l.code === lang;
            return (
              <LangItem
                key={l.code}
                s={s}
                lang={l}
                href={hrefFor(l.code)}
                active={active}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function LangItem({ s, lang, href, active }) {
  const [hover, setHover] = useState(false);
  const style = {
    ...s.langItem,
    ...(active ? s.langItemActive : null),
    ...(hover && !active ? { backgroundColor: C.zinc50 } : null),
  };
  return (
    <a
      href={href}
      hrefLang={lang.code}
      lang={lang.code}
      role="option"
      aria-selected={active}
      aria-current={active ? "page" : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={style}
    >
      <span>{lang.native}</span>
      {active ? <CheckIcon style={s.langCheck} /> : <span style={s.langCode}>{lang.short}</span>}
    </a>
  );
}

/* -------------------------------------------------------------------------- */
/* Sections                                                                    */
/* -------------------------------------------------------------------------- */

function Navbar({ s, t, mdUp, lang, hrefFor }) {
  const home = langPath(lang);
  const practice = { href: practicePath(lang), label: PRACTICE[lang].navLabel };
  const [open, setOpen] = useState(false);
  const n = t.nav;

  return (
    <nav style={s.nav} data-track-placement="navbar">
      <div style={s.navGrid}>
        <a href={home} style={s.navLogo}>
          <Picture src={IMG.logo} alt="Teorikörkort" sizes="80px" loading="eager" style={s.navLogoImg} />
        </a>

        <div style={s.navLinks}>
          <HoverLink href={home} base={s.navLink} hover={s.navLinkHover}>{n.home}</HoverLink>
          <HoverLink href={`${home}#features`} base={s.navLink} hover={s.navLinkHover}>{n.features}</HoverLink>
          <HoverLink href={practice.href} base={s.navLink} hover={s.navLinkHover}>{practice.label}</HoverLink>
          <HoverLink href={`${home}#faq`} base={s.navLink} hover={s.navLinkHover}>{n.faq}</HoverLink>
          <HoverLink href={`mailto:${CONTACT_EMAIL}`} base={s.navLink} hover={s.navLinkHover}>{n.contact}</HoverLink>
        </div>

        <div style={s.navRight}>
          <div style={s.navStores}>
            <HoverLink href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" base={s.badgeLinkBase} hover={s.badgeLinkHover}>
              <img src={IMG.apple} alt={t.a11y.appStore} width="120" height="40" style={s.navApple} />
            </HoverLink>
            <HoverLink href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" base={s.badgeLinkBase} hover={s.badgeLinkHover}>
              <img src={IMG.google} alt={t.a11y.googlePlay} width="646" height="250" style={s.navGoogle} />
            </HoverLink>
          </div>

          <LangSwitcher s={s} t={t} lang={lang} hrefFor={hrefFor} />

          <button style={s.burger} onClick={() => setOpen(!open)} aria-label={t.a11y.menu} aria-expanded={open}>
            {open ? <XMarkIcon style={s.burgerIcon} /> : <Bars3Icon style={s.burgerIcon} />}
          </button>
        </div>
      </div>

      {!mdUp && open && (
        <div style={s.mobileMenu}>
          <div style={s.mobileInner}>
            <HoverLink href={home} base={s.mobileLink} hover={s.navLinkHover} onClick={() => setOpen(false)}>{n.home}</HoverLink>
            <HoverLink href={`${home}#features`} base={s.mobileLink} hover={s.navLinkHover} onClick={() => setOpen(false)}>{n.features}</HoverLink>
            <HoverLink href={practice.href} base={s.mobileLink} hover={s.navLinkHover} onClick={() => setOpen(false)}>{practice.label}</HoverLink>
            <HoverLink href={`${home}#faq`} base={s.mobileLink} hover={s.navLinkHover} onClick={() => setOpen(false)}>{n.faq}</HoverLink>
            <HoverLink href={`mailto:${CONTACT_EMAIL}`} base={s.mobileLink} hover={s.navLinkHover} onClick={() => setOpen(false)}>{n.contact}</HoverLink>
            <div style={s.mobileStores}>
              <HoverLink href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" base={s.badgeLinkBase} hover={s.badgeLinkHover}>
                <img src={IMG.apple} alt={t.a11y.appStore} width="120" height="40" style={s.navApple} />
              </HoverLink>
              <HoverLink href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" base={s.badgeLinkBase} hover={s.badgeLinkHover}>
                <img src={IMG.google} alt={t.a11y.googlePlay} width="646" height="250" style={s.navGoogle} />
              </HoverLink>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

function Hero({ s, t, lang }) {
  const h = t.hero;
  return (
    <main style={s.hero}>
      <div style={s.heroGlow} aria-hidden={true} />
      <div style={s.heroContainer}>
        <div style={s.heroGrid}>
          <div style={s.heroCopy}>
            <div style={s.heroSocial}>
              <div style={s.heroSocialAvatars}>
                {REVIEW_AVATARS.map((src, i) => (
                  <Picture
                    key={i}
                    src={src}
                    alt=""
                    sizes="36px"
                    loading="eager"
                    style={{ ...s.heroSocialAvatar, position: "relative", marginLeft: i === 0 ? 0 : "-.6rem", zIndex: REVIEW_AVATARS.length - i }}
                  />
                ))}
              </div>
              <div style={s.heroSocialTextWrap}>
                <Stars style={s.heroSocialStars} starStyle={s.heroSocialStar} />
                <span style={s.heroSocialText}>{h.social}</span>
              </div>
            </div>
            <h1 style={s.heroTitle}>
              {h.titlePre}<em style={s.em}>{h.titleEm}</em>
              <br />
              {h.titlePost}
            </h1>
            <p style={s.heroLead}>{h.lead}</p>
            <div style={s.heroCta}>
              <div style={s.heroBuy} data-track-placement="hero">
                <span className="tk-buy-badge" style={s.buyBadge}>{h.buyLabel}</span>
                <StoreBadges s={s} t={t} eager />
                <span style={s.heroBuyNote}>{h.buyNote}</span>
                <a href={practicePath(lang)} style={s.heroPracticeLink}>{PRACTICE[lang].heroLink}</a>
              </div>
            </div>
          </div>

          <div style={s.heroMediaCol}>
            <div style={s.heroBlur} aria-hidden={true} />
            <div style={s.heroStage}>
              <Picture src={IMG.screen1} alt={h.altHome} sizes="(min-width: 640px) 310px, 190px" loading="eager" fetchPriority="high" style={{ ...s.phoneBase, ...s.phoneLeft, height: "auto" }} />
              <Picture src={IMG.screen2} alt={h.altQuestion} sizes="(min-width: 640px) 310px, 190px" loading="eager" style={{ ...s.phoneBase, ...s.phoneRight, height: "auto" }} />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function HowItWorks({ s, t }) {
  const w = t.how;
  return (
    <section id="how-it-works" style={s.section}>
      <div style={s.container}>
        <div style={s.sectionHead}>
          <span style={s.tag}>{w.tag}</span>
          <h2 style={s.h2}>{w.titlePre}<em style={s.em}>{w.titleEm}</em>{w.titlePost}</h2>
          <p style={s.sectionLead}>{w.lead}</p>
        </div>
        <div style={s.steps}>
          {w.steps.map((step, i) => (
            <div key={i} style={s.step}>
              <div style={s.stepMedia}>
                <span style={s.stepNum}>{STEP_NUMS[i]}</span>
                <Picture src={STEP_IMAGES[i]} alt={step.alt} sizes="(min-width: 1024px) 24rem, 90vw" style={s.stepImg} />
              </div>
              <h3 style={s.stepTitle}>{step.title}</h3>
              <p style={s.stepDesc}>{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features({ s, t }) {
  const f = t.features;
  return (
    <section id="features" style={s.section}>
      <div style={s.container}>
        <div style={s.sectionHead}>
          <span style={s.tag}>{f.tag}</span>
          <h2 style={s.h2}>{f.titlePre}<em style={s.em}>{f.titleEm}</em></h2>
        </div>
        <div style={s.features}>
          {f.items.map((feature, i) => {
            const Icon = FEATURE_ICONS[i];
            return (
              <div key={i} style={s.feature}>
                <div style={s.featureIconBox}>
                  <Icon style={s.featureIcon} strokeWidth={1.75} />
                </div>
                <h3 style={s.featureTitle}>{feature.name}</h3>
                <p style={s.featureDesc}>{feature.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Reviews({ s, t }) {
  const r = t.reviews;
  return (
    <section id="reviews" style={s.section}>
      <div style={s.container}>
        <div style={s.sectionHead}>
          <span style={s.tag}>{r.tag}</span>
          <h2 style={s.h2}>{r.heading}</h2>
        </div>
        <div style={s.reviews}>
          {r.items.map((item, i) => (
            <div key={i} style={s.reviewCard}>
              <Stars count={REVIEW_STARS[i]} style={s.reviewStars} starStyle={s.reviewStar} />
              <p style={s.reviewText}>{item.text}</p>
              <div style={s.reviewer}>
                <Picture src={REVIEW_AVATARS[i]} alt={REVIEW_NAMES[i]} sizes="44px" style={s.reviewerAvatar} />
                <div>
                  <div style={s.reviewerName}>{REVIEW_NAMES[i]}</div>
                  <div style={s.reviewerLabel}>{item.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing({ s, t }) {
  const p = t.pricing;
  return (
    <section id="pricing" style={s.section}>
      <div style={s.container}>
        <div style={s.sectionHead}>
          <span style={s.tag}>{p.tag}</span>
          <h2 style={s.h2}>{p.heading}</h2>
          <p style={s.sectionLead}>{p.lead}</p>
        </div>
        <div style={s.pricing}>
          {p.plans.map((plan, i) => {
            const meta = PLAN_META[i];
            const f = meta.featured;
            return (
              <div key={i} style={{ ...s.priceCard, ...(f ? s.priceCardFeatured : null) }}>
                {plan.badge ? (
                  <span style={f ? s.priceBadgeFeatured : s.priceBadge}>{plan.badge}</span>
                ) : null}
                <h3 style={f ? s.priceNameFeatured : s.priceName}>{plan.name}</h3>
                <div style={s.priceAmountRow}>
                  <span style={f ? s.priceAmountFeatured : s.priceAmount}>{meta.amount} kr</span>
                </div>
                <span style={f ? s.pricePeriodFeatured : s.pricePeriod}>{plan.period}</span>
                <p style={f ? s.priceNoteFeatured : s.priceNote}>{plan.note}</p>
              </div>
            );
          })}
        </div>
        <div style={s.priceBuy} data-track-placement="pricing">
          <span className="tk-buy-badge" style={s.buyBadge}>{p.buyLabel}</span>
          <StoreBadges s={s} t={t} center />
          <span style={s.priceBuyNote}>{p.buyNote}</span>
        </div>
      </div>
    </section>
  );
}

function FaqItem({ item, open, onToggle, isLast, s }) {
  const [hover, setHover] = useState(false);
  const chevColor = open || hover ? C.zinc900 : C.zinc500;
  return (
    <div style={{ borderBottom: isLast ? "none" : `1px solid ${C.zinc200}` }}>
      <button
        onClick={onToggle}
        aria-expanded={open}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{ ...s.faqButton, backgroundColor: hover ? C.zinc50 : "transparent" }}
      >
        <span style={s.faqQuestion}>{item.question}</span>
        <ChevronDownIcon strokeWidth={2} style={{ ...s.faqChevron, color: chevColor, transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      <div style={s.faqAnswer(open)}>
        <p style={s.faqAnswerText}>{item.answer}</p>
      </div>
    </div>
  );
}

function FAQ({ s, t }) {
  const [openIndex, setOpenIndex] = useState(null);
  const f = t.faq;
  return (
    <section id="faq" style={s.section}>
      <div style={s.faqContainer}>
        <div style={s.faqHead}>
          <span style={s.tag}>{f.tag}</span>
          <h2 style={s.h2}>{f.heading}</h2>
        </div>
        <div style={s.faqList}>
          {f.items.map((item, i) => (
            <FaqItem
              key={i}
              item={item}
              open={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
              isLast={i === f.items.length - 1}
              s={s}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer({ s, t, lang, hrefFor }) {
  const f = t.footer;
  return (
    <footer style={s.footer} data-track-placement="footer">
      <div style={s.footerContainer}>
        <div style={s.footerTop}>
          <div style={s.footerBrand}>
            <Picture src={IMG.logo} alt="Teorikörkort" sizes="48px" style={s.footerLogo} />
            <StoreBadges s={s} t={t} />
          </div>
          <div style={s.footerCols}>
            <div style={s.footerCol}>
              <h2 style={s.footerColTitle}>{f.legal}</h2>
              <ul style={s.footerList}>
                <li><HoverLink href="/privacy" base={s.footerLink} hover={s.footerLinkHover}>{f.privacy}</HoverLink></li>
                <li><HoverLink href="/terms-of-service" base={s.footerLink} hover={s.footerLinkHover}>{f.terms}</HoverLink></li>
                {ANALYTICS_ENABLED && <li><button type="button" onClick={openConsentSettings} style={{ ...s.footerLink, background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit" }}>{COOKIE_SETTINGS_LABEL[lang]}</button></li>}
              </ul>
            </div>
            <div style={s.footerCol}>
              <h2 style={s.footerColTitle}>{f.company}</h2>
              <ul style={s.footerList}>
                <li><HoverLink href={practicePath(lang)} base={s.footerLink} hover={s.footerLinkHover}>{PRACTICE[lang].navLabel}</HoverLink></li>
                <li><HoverLink href={`mailto:${CONTACT_EMAIL}`} base={s.footerLink} hover={s.footerLinkHover}>{f.contact}</HoverLink></li>
                <li><HoverLink href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" base={s.footerLink} hover={s.footerLinkHover}>Instagram</HoverLink></li>
              </ul>
            </div>
            <div style={s.footerCol}>
              <h2 style={s.footerColTitle}>{t.a11y.lang}</h2>
              <ul style={s.footerList}>
                {LANGS.map((l) => (
                  <li key={l.code}>
                    <HoverLink
                      href={hrefFor(l.code)}
                      hrefLang={l.code}
                      lang={l.code}
                      aria-current={l.code === lang ? "page" : undefined}
                      base={s.footerLink}
                      hover={s.footerLinkHover}
                    >
                      {l.native}
                    </HoverLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div style={s.footerBottom}>
          <p style={s.footerCopy}>© {new Date().getFullYear()} Teorikörkort. {f.rights}</p>
        </div>
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------------------- */
/* SEO                                                                         */
/* -------------------------------------------------------------------------- */

/* Title, description, canonical, hreflang, Open Graph and structured data are
   written into each page's static HTML by scripts/prerender.js (see
   src/seo/site.js). This only keeps the document in sync during development. */
function useDocumentLang(lang, t) {
  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = t.seo.title;
  }, [lang, t]);
}

/* -------------------------------------------------------------------------- */
/* Page                                                                        */
/* -------------------------------------------------------------------------- */

const RESET = `
.tk-page *, .tk-page *::before, .tk-page *::after { box-sizing: border-box; margin: 0; padding: 0; border: 0 solid; }
.tk-page ul { list-style: none; }
.tk-page a { color: inherit; text-decoration: none; }
.tk-page button { font: inherit; }
.tk-page img, .tk-page svg { display: block; vertical-align: middle; }
.tk-page img { max-width: 100%; }
.tk-page a:focus-visible, .tk-page button:focus-visible { outline: 2px solid #18181b; outline-offset: 2px; }
.tk-buy-badge { box-shadow: 0 6px 18px -6px rgba(251,191,36,.55); animation: tkBuyGlow 2.4s ease-in-out infinite; }
@keyframes tkBuyGlow { 0%,100% { box-shadow: 0 6px 18px -6px rgba(251,191,36,.5); } 50% { box-shadow: 0 10px 28px -6px rgba(251,191,36,.85); } }
@media (prefers-reduced-motion: reduce) { .tk-page * { transition-duration: .01ms !important; } .tk-buy-badge { animation: none; } }
`;

export default function Landing({ lang: langProp }) {
  const bp = useBreakpoint();
  const mdUp = bp === "md" || bp === "lg";
  const s = makeStyles(bp);
  const lang = LANG_CODES.includes(langProp) ? langProp : DEFAULT_LANG;
  const t = TR[lang];

  useDocumentLang(lang, t);

  // Load Poppins once, without an @import (more reliable across setups).
  useEffect(() => {
    const id = "tk-poppins-font";
    if (document.getElementById(id)) return;
    const pre1 = document.createElement("link");
    pre1.rel = "preconnect";
    pre1.href = "https://fonts.googleapis.com";
    const pre2 = document.createElement("link");
    pre2.rel = "preconnect";
    pre2.href = "https://fonts.gstatic.com";
    pre2.crossOrigin = "anonymous";
    const font = document.createElement("link");
    font.id = id;
    font.rel = "stylesheet";
    font.href = "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=optional";
    document.head.append(pre1, pre2, font);
  }, []);

  return (
    <div className="tk-page" style={s.page}>
      <style>{RESET}</style>

      <Navbar s={s} t={t} mdUp={mdUp} lang={lang} hrefFor={langPath} />

      <div style={s.sections}>
        <div style={{ marginBottom: "12rem" }}><Hero s={s} t={t} lang={lang} /></div>
        <div style={{ marginBottom: "12rem" }}><HowItWorks s={s} t={t} /></div>
        <div style={{ marginBottom: "12rem" }}><Features s={s} t={t} /></div>
        <div style={{ marginBottom: "12rem" }}><Reviews s={s} t={t} /></div>
        <div style={{ marginBottom: "12rem" }}><Pricing s={s} t={t} /></div>
        <div style={{ marginBottom: "12rem" }}><FAQ s={s} t={t} /></div>
        <Footer s={s} t={t} lang={lang} hrefFor={langPath} />
      </div>
    </div>
  );
}

/* Shared page frame — the landing page's navbar and footer — for other public
   pages such as the practice test. `hrefFor(code)` returns the URL of the same
   page in another language; the language menu and footer link to it. */
export function SiteShell({ lang, hrefFor, children }) {
  const bp = useBreakpoint();
  const mdUp = bp === "md" || bp === "lg";
  const s = makeStyles(bp);
  const t = TR[lang] || TR[DEFAULT_LANG];

  return (
    <div className="tk-page" style={s.page}>
      <style>{RESET}</style>
      <Navbar s={s} t={t} mdUp={mdUp} lang={lang} hrefFor={hrefFor} />
      {children}
      <Footer s={s} t={t} lang={lang} hrefFor={hrefFor} />
    </div>
  );
}